import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Seo from "@/components/site/Seo";
import Band, { Eyebrow } from "@/components/site/Band";
import Calculator from "@/components/site/Calculator";
import { useCalculatorConfig, usePartners } from "@/hooks/useSiteData";
import { HOW_STEPS, PROOF, OG } from "@/lib/content";

const Door = ({ to, kicker, headline, sub, testId }) => (
  <Link
    to={to}
    data-testid={testId}
    className="group relative flex flex-col justify-between p-8 sm:p-10 lg:p-14 min-h-[300px] lg:min-h-[440px] bg-bone hover:bg-spruce transition-colors duration-300"
  >
    <div className="tabular text-xs uppercase tracking-[0.2em] text-sage group-hover:text-sage">
      {kicker}
    </div>
    <div>
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-spruce group-hover:text-bone leading-[1.05] mb-4 transition-colors">
        {headline}
      </h2>
      <p className="text-sm text-ink/70 group-hover:text-sage max-w-sm mb-6 transition-colors">{sub}</p>
      <span className="inline-flex items-center gap-2 text-sm font-semibold text-spruce group-hover:text-brass transition-colors">
        {to === "/340b" ? "Go to 340B" : "Go to RPM"}
        <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
      </span>
    </div>
  </Link>
);

export default function Home() {
  const { data: config } = useCalculatorConfig();
  const { data: partners } = usePartners();

  return (
    <>
      <Seo
        title="Practices meet hospital-system 340B and RPM programs"
        description="Health and More connects independent physician practices to hospital-system 340B pharmacy and remote patient monitoring programs, with the administrative staff to run them."
        path="/"
        ogImage={OG.default}
      />

      {/* Two-door split — above the fold */}
      <section className="band bg-spruce" data-testid="hero-doors">
        <div className="band-inner py-10 lg:py-12">
          <p className="text-bone/90 text-base sm:text-lg max-w-2xl mb-8">
            Two programs, two kinds of practice. Start with the one that describes your revenue.
          </p>
          <div className="grid md:grid-cols-2 border border-sage/40 rounded-[2px] overflow-hidden divide-y md:divide-y-0 md:divide-x divide-sage/40">
            <Door
              to="/340b"
              testId="door-340b"
              kicker="Door one"
              headline="Most of my revenue is specialty drugs"
              sub="Hematology, oncology, nephrology, pulmonary, rheumatology. 340B pricing through our hospital partners."
            />
            <Door
              to="/rpm"
              testId="door-rpm"
              kicker="Door two"
              headline="Most of my revenue is patient visits"
              sub="Internal medicine, family practice, cardiology, endocrinology, nephrology. Remote monitoring for a chronic-care panel."
            />
          </div>
          <p className="text-sage text-sm mt-4">
            Nephrology fits both.{" "}
            <Link to="/network" className="text-bone underline underline-offset-4 decoration-sage hover:decoration-brass" data-testid="hero-dual-path">
              See how a dual-path practice is handled
            </Link>
            .
          </p>
        </div>
      </section>

      {/* Calculator — the substance of the hero */}
      <Band tone="bone" id="calculator">
        <Eyebrow>The math, before the sales call</Eyebrow>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-spruce leading-[1.05] mb-4 max-w-2xl">
          Run your own numbers. Every assumption is on the screen.
        </h2>
        <p className="text-base text-ink/80 max-w-2xl mb-10">
          Most vendor calculators hide the enrollment rate and the compliance threshold to make the
          output look bigger. Ours don't. Lower them and watch the estimate drop. That is the point.
        </p>
        {config && <Calculator config={config} />}
      </Band>

      {/* MSO objection-handler */}
      <Band tone="spruce">
        <div className="max-w-3xl">
          <Eyebrow>Administrative services</Eyebrow>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] mb-6">
            You don't have the staff for this. That's what we're for.
          </h2>
          <p className="text-bone/85 text-base mb-10 max-w-2xl">
            Neither program works if it lands on your front desk. We bring the people and the process,
            so nothing new gets added to your team's day.
          </p>
          <ul className="grid sm:grid-cols-2 gap-x-10 gap-y-5 max-w-2xl">
            {[
              "We ship the devices and call the patient who stopped transmitting.",
              "We document the 20 minutes and keep the billing clean.",
              "We run credentialing, payer enrollment, and the 340B split-billing setup.",
              "We keep the audit file ready before anyone asks for it.",
            ].map((t, i) => (
              <li key={i} className="flex gap-3 text-sm text-bone/90">
                <span className="tabular text-brass shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
          <Link
            to="/mso"
            className="mt-10 inline-flex items-center gap-2 text-sm font-semibold text-spruce bg-brass px-5 py-3 rounded-[2px] hover:brightness-95 transition-all"
            data-testid="home-mso-cta"
          >
            What we take off your plate
            <ArrowRight size={16} />
          </Link>
        </div>
      </Band>

      {/* Named partners */}
      <Band tone="bone">
        <Eyebrow>The network</Eyebrow>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-spruce leading-[1.05] mb-6 max-w-2xl">
          We work through named hospital systems. We can name them.
        </h2>
        <p className="text-base text-ink/80 max-w-2xl mb-10">
          Any competitor can claim 340B expertise. Almost none can tell you which covered entities they
          work through. Here are ours.
        </p>
        <div className="grid md:grid-cols-3 gap-px bg-sage/40 border border-sage/40 rounded-[2px] overflow-hidden">
          {(partners || []).map((p) => (
            <div key={p.id} className="bg-bone p-6" data-testid={`home-partner-${p.id}`}>
              <div className="tabular text-xs text-sage uppercase tracking-wider mb-2">{p.market}</div>
              <h3 className="font-display text-2xl text-spruce mb-3">{p.name}</h3>
              <p className="text-sm text-ink/80 leading-relaxed">{p.summary}</p>
            </div>
          ))}
        </div>
        <Link
          to="/network"
          className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-spruce hover:text-brass transition-colors group"
          data-testid="home-network-link"
        >
          Full network and geography
          <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
      </Band>

      {/* Proof — network / partner experience */}
      <Band tone="bone">
        <Eyebrow>Network experience</Eyebrow>
        <h2 className="font-display text-3xl sm:text-4xl text-spruce leading-[1.05] mb-4 max-w-2xl">
          The track record behind the programs.
        </h2>
        <p className="text-base text-ink/80 max-w-2xl mb-12">
          These figures reflect our hospital partners and the operating team's experience — the people and
          systems your program actually runs on.
        </p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-sage/40 border border-sage/40 rounded-[2px] overflow-hidden">
          {PROOF.map((p, i) => (
            <div key={i} className="bg-bone p-6 sm:p-8" data-testid={`proof-${i}`}>
              <div className="tabular text-4xl sm:text-5xl text-brass font-medium leading-none mb-3">{p.value}</div>
              <div className="text-sm font-semibold text-spruce mb-1">{p.label}</div>
              <div className="text-xs text-sage leading-relaxed">{p.note}</div>
            </div>
          ))}
        </div>
      </Band>

      {/* How it starts */}
      <Band tone="ink">
        <Eyebrow>How it starts</Eyebrow>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] mb-12 max-w-2xl">
          Three steps. Real weeks, not the word "fast."
        </h2>
        <div className="grid sm:grid-cols-3 gap-8 mb-12">
          {HOW_STEPS.slice(0, 3).map((s, i) => (
            <div key={i} className="border-t border-sage/40 pt-5" data-testid={`home-step-${i}`}>
              <div className="tabular text-brass text-sm mb-2">{s.week}</div>
              <h3 className="text-lg font-semibold mb-2">{s.title}</h3>
              <p className="text-sm text-sage leading-relaxed">{s.us}</p>
            </div>
          ))}
        </div>
        <Link
          to="/contact"
          className="inline-flex items-center gap-2 text-sm font-semibold text-spruce bg-brass px-6 py-3 rounded-[2px] hover:brightness-95 transition-all"
          data-testid="home-final-cta"
        >
          Start the conversation
          <ArrowRight size={16} />
        </Link>
      </Band>
    </>
  );
}
